import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useDynamicConfig, useStatsigClient } from '@statsig/react-bindings';
import { useLocation } from 'react-router-dom';
import { buildAssistantHeaders, buildAssistantUrl } from "../../utils/assistantApi";
import { useAssistantAbly } from "../../contexts/AIAssistantAblyContext";
import { sendUserMessage, stopCurrentTurn } from "../../state/conversationActions";
import {
    addMessage,
    userTyping,
    assistantResponding,
    addSelectedValue,
    removeSelectedValue,
    removeAllSelectedValues,
    setActiveReport,
    addErrorMessage,
    clearErrorMessage,
    setTaskStatusMessage,
    setHistoryMetadata,
    setLastMessageTraceId,
    updateReportMessage,
    setFollowupSuggestions,
    clearFollowupSuggestions,
    attachQueryParams,
} from "../../state/actions";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";
import { getLogoUrlFromArticle } from "../../utils/logoDev";
import ErrorCodes from '@/constants/errors/ErrorCodes';
import { setPendingSelectionIfChanged } from "@/store/slices/pendingSelection/pendingSelectionSlice";
import { parseURLParams } from "@/utils/url";
import HistoryMetaData from "../HistoryMetaData";
import Tooltip from "../Tooltip";
import {
    FiArrowUp,
    FiSquare,
    FiFileText,
    FiBarChart2,
    FiPlus,
    FiSliders,
    FiRss,
} from "react-icons/fi";
import { useScreenContext } from "../../hooks/useScreenContext";
import VoiceInputControl from "../VoiceInputControl";
import { AI_ASSISTANT_PENDO_EVENTS, trackAssistantEvent } from "../../utils/assistantAnalytics";
import "./MessageInput.scss";

const NEWS_SEARCH_VALUE = AI_ASSISTANT_CONSTANTS.SELECTED_VALUES.NEWS_SEARCH;
/** Shown in the composer when the assistant cannot be reached at all. */
const UNAVAILABLE_PLACEHOLDER = "Lou is unavailable right now";
const VOICE_INPUT_STATUS = {
    IDLE: "idle",
    LISTENING: "listening",
    LIMIT_REACHED: "limit_reached",
    TRANSCRIBING: "transcribing",
    MIC_BLOCKED: "mic_blocked",
    MIC_UNAVAILABLE: "mic_unavailable",
    TRANSCRIPTION_FAILED: "transcription_failed",
};

const MessageInput = () => {
    const navigate = useNavigate();
    const { checkGate } = useStatsigClient();
    const config = useDynamicConfig(AI_ASSISTANT_CONSTANTS.DYNAMIC_CONFIGS.AI_ASSISTANT_CONFIGS);
    const location = useLocation();
    const [messageInput, setMessageInput] = useState("");
    const [isVoiceInputActive, setIsVoiceInputActive] = useState(false);
    const [voiceInputStatus, setVoiceInputStatus] = useState(VOICE_INPUT_STATUS.IDLE);
    const [isNewsSearchEnabled, setIsNewsSearchEnabled] = useState(false);
    const dispatch = useDispatch();
    const typingTimeoutRef = useRef(null);
    const inputRef = useRef(null);
    const voicePanelHostRef = useRef(null);
    const voiceInsertionRangeRef = useRef(null);
    const [isInputFocused, setIsInputFocused] = useState(false);
    // Which turn the user has already asked to stop. The turn does not end
    // until the backend says so on the channel, so without this the button
    // stays live and invites a second press that does nothing.
    const [stopRequestedFor, setStopRequestedFor] = useState(null);
    const { selectedValues, historyMetadata, isAssistantResponding, activeTurnId } = useSelector((state) => state.chat);
    const userDetail = useSelector((state) => state.userDetail);
    const { conversationId, isReachable } = useAssistantAbly();
    const isSummaryButtonEnabled = checkGate(
        AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_SUMMARISE_SCREEN_ENABLED
    );
    const isReportBuilderButtonEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_REPORT_BUILDER_ENABLED);
    const isHistoryMetadataContextEnabled = checkGate(AI_ASSISTANT_CONSTANTS.FEATURE_FLAGS.AI_ASSISTANT_HISTORY_METADATA_CONTEXT_ENABLED)
    const isVoiceInputEnabled = import.meta.env.VITE_ENV?.toLowerCase() === "development";
    const summaryHiddenRoutes = ["/reports"];
    const shouldHideSummary = summaryHiddenRoutes.some((route) =>
        location?.pathname?.startsWith(route)
    );
    const shouldShowButton = isSummaryButtonEnabled && !shouldHideSummary;
    const shouldShowReportBuilderButton = isReportBuilderButtonEnabled;
    const disableNewsSearchButton = false;
    const disableSummaryButton = false;
    const disableReportBuilderButton = true;
    const isNewsSearchActive = selectedValues.includes(NEWS_SEARCH_VALUE);
    const [toolsDropdownOpen, setToolsDropdownOpen] = useState(false);
    const toolsDropdownRef = useRef(null);
    const messageSelectedValues = selectedValues.filter(
        (value) => value !== NEWS_SEARCH_VALUE
    );
    // Unreachable is different from busy. When the assistant is down there is
    // nowhere for the text to go, so the composer says so rather than taking
    // a message and losing it.
    const isUnavailable = isReachable === false;
    const isSendDisabled =
        isAssistantResponding ||
        isVoiceInputActive ||
        isUnavailable ||
        messageInput.trim().length === 0;
    // Shown in place of send while a turn runs, and only when there is a turn
    // to stop - a reply arriving from another device is not this page's to end.
    const canStopTurn = isAssistantResponding && Boolean(activeTurnId) && Boolean(conversationId);
    const isStopping = canStopTurn && stopRequestedFor === activeTurnId;
    const isSummaryActionDisabled =
        disableSummaryButton ||
        isAssistantResponding;
    const { buildCurrentScreenContext } = useScreenContext();
    const convertQueryParamsToObject = (searchString = window.location.search) => {
        const urlParams = new URLSearchParams(searchString);
        const paramsWithArrays = {};
        for (const [key, value] of urlParams) {
            if (paramsWithArrays[key]) {

                paramsWithArrays[key] = Array.isArray(paramsWithArrays[key])
                    ? [...paramsWithArrays[key], value]
                    : [paramsWithArrays[key], value];
            } else {
                paramsWithArrays[key] = value;
            }
        }
        return paramsWithArrays;
    };
    const createInitialReportState = (reportId) => ({
        report_id: reportId,
        title: "Building your report",
        description: "Gathering the freshest insights from this view.",
        sections: [],
        plan: null,
        status_message: "Initializing workspace...",
        build_progress: {
            currentStage: "initializing",
            startedAt: new Date().toISOString(),
            log: [],
        },
    });
    const toPlaceholderSections = (sections = []) =>
        sections
            .filter((section) => typeof section === "object" && section !== null)
            .map((section, idx) => ({
                section_id: `planned-${idx + 1}`,
                title: section.title || `Section ${idx + 1}`,
                objective: section.objective,
                focus_metrics: section.focus_metrics || [],
                visuals: section.visuals || [],
                is_placeholder: true,
                placeholder_status: "pending",
                plan_index: idx + 1,
                blocks: [],
            }));
    useEffect(() => {
        if (messageInput.length > 0 || isInputFocused) {
            dispatch(userTyping(true));
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => {
                dispatch(userTyping(false));
            }, 3000);
        } else {
            dispatch(userTyping(false));
        }
        return () => clearTimeout(typingTimeoutRef.current);
    }, [messageInput, isInputFocused, dispatch]);
    useEffect(() => {
        const textarea = inputRef.current;
        if (!textarea) return;
        const resizeObserver = new ResizeObserver(() => {
            adjustTextareaHeight();
        });
        resizeObserver.observe(textarea);
        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        if (!toolsDropdownOpen) return;
        const handleClickOutside = (event) => {
            if (
                toolsDropdownRef.current &&
                !toolsDropdownRef.current.contains(event.target)
            ) {
                setToolsDropdownOpen(false);
            }
        };
        const handleEscape = (event) => {
            if (event.key === "Escape") setToolsDropdownOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleEscape);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleEscape);
        };
    }, [toolsDropdownOpen]);
    const resetTextareaHeight = () => {
        if (inputRef.current) {
            inputRef.current.style.height = "auto";
            inputRef.current.style.overflowY = "hidden";
        }
    };

    const scrollMessagesToBottom = () => {
        const container = document.querySelector(".msg-container");
        if (container) {
            container.scrollTop = container.scrollHeight;
        }
    };

    // The SSE reader that used to live here is gone. A reply is no longer
    // returned on the request that asked for it: it arrives as
    // `conversation.*` commands on the conversation's Ably channel, is routed
    // by the Lou command bridge, and lands in the store keyed by turn id.
    // See louCommandBridge/commandRouter.ts and state/conversationActions.js.
    const streamReportBuild = async (clientReportId, requestPayload, initialReportState) => {
        let reportSnapshot = { ...initialReportState };
        let statusMessage = "Initializing workspace...";
        let buildCompleted = false;

        const pushUpdate = (overrides = {}) => {
            dispatch(updateReportMessage(clientReportId, {
                report_data: { ...reportSnapshot },
                report_status: overrides.report_status ?? "processing",
                report_status_message: overrides.report_status_message ?? statusMessage,
                report_title: reportSnapshot.title,
            }));
        };

        const response = await fetch(
            buildAssistantUrl("build-report/"),
            {
                method: "POST",
                headers: await buildAssistantHeaders(),
                credentials: "include",
                body: JSON.stringify({ ...requestPayload, clientReportId }),
            }
        );

        if (!response.ok) {
            const err = new Error(response.statusText || "Server error");
            err.origin = "backend";
            throw err;
        }

        if (!response.body) {
            throw new Error("ReadableStream not supported");
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let buffer = "";
        pushUpdate({ report_status_message: statusMessage });

        while (true) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            const parts = buffer.split("\n");
            buffer = parts.pop();
            for (const part of parts) {
                const trimmed = part.trim();
                if (!trimmed) {
                    continue;
                }
                let chunkJson;
                try {
                    chunkJson = JSON.parse(trimmed);
                } catch (error) {
                    console.warn("Invalid JSON chunk from report stream", error);
                    continue;
                }
                if (chunkJson.client_report_id && chunkJson.client_report_id !== clientReportId) {
                    continue;
                }
                const { type } = chunkJson;
                if (type === "report_status") {
                    const stage = chunkJson.stage || reportSnapshot.build_progress?.currentStage || "processing";
                    statusMessage = chunkJson.message || statusMessage;
                    const logEntry = {
                        stage,
                        message: statusMessage,
                        timestamp: new Date().toISOString(),
                        detail: chunkJson.detail,
                    };
                    const prevLog = reportSnapshot.build_progress?.log || [];
                    const nextLog = [...prevLog, logEntry].slice(-25);
                    reportSnapshot = {
                        ...reportSnapshot,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: stage,
                            lastUpdate: new Date().toISOString(),
                            log: nextLog,
                            totalSections: chunkJson.total_sections ?? reportSnapshot.build_progress?.totalSections,
                            completedSections: chunkJson.completed_sections ?? reportSnapshot.build_progress?.completedSections,
                        },
                    };
                    pushUpdate({ report_status_message: statusMessage });
                } else if (type === "report_plan") {
                    const planSections = toPlaceholderSections(chunkJson.sections || []);
                    reportSnapshot = {
                        ...reportSnapshot,
                        title: chunkJson.title || reportSnapshot.title,
                        description: chunkJson.description || reportSnapshot.description,
                        plan: {
                            title: chunkJson.title,
                            description: chunkJson.description,
                            sections: chunkJson.sections || [],
                        },
                        sections: planSections,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            totalSections: planSections.length || reportSnapshot.build_progress?.totalSections,
                        },
                    };
                    pushUpdate();
                } else if (type === "report_section") {
                    const sectionData = chunkJson.section;
                    if (!sectionData) {
                        continue;
                    }
                    const targetIndex = Math.max((chunkJson.index || 1) - 1, 0);
                    const existingSections = reportSnapshot.sections ? [...reportSnapshot.sections] : [];
                    if (targetIndex >= existingSections.length) {
                        existingSections[targetIndex] = { ...sectionData, is_placeholder: false };
                    } else {
                        existingSections[targetIndex] = { ...sectionData, is_placeholder: false };
                    }
                    const completed = chunkJson.completed_sections ?? reportSnapshot.build_progress?.completedSections ?? targetIndex + 1;
                    const total = chunkJson.total_sections ?? reportSnapshot.build_progress?.totalSections ?? existingSections.length;
                    statusMessage = `Section ${completed}/${total} finalized`;
                    reportSnapshot = {
                        ...reportSnapshot,
                        sections: existingSections,
                        status_message: statusMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            completedSections: completed,
                            totalSections: total,
                            currentStage: "section_ready",
                        },
                    };
                    pushUpdate({ report_status_message: statusMessage });
                } else if (type === "report_complete") {
                    const finalReport = chunkJson.report || {};
                    const finalReportId = finalReport.report_id || clientReportId;
                    const mergedReport = {
                        ...finalReport,
                        plan: reportSnapshot.plan,
                        status_message: "Report ready",
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: "completed",
                            completedAt: new Date().toISOString(),
                            log: [
                                ...(reportSnapshot.build_progress?.log || []),
                                {
                                    stage: "completed",
                                    message: "Report ready",
                                    timestamp: new Date().toISOString(),
                                },
                            ].slice(-25),
                        },
                    };
                    reportSnapshot = mergedReport;
                    dispatch(updateReportMessage(clientReportId, {
                        report_id: finalReportId,
                        report_status: "success",
                        report_status_message: "Report ready",
                        report_title: mergedReport.title || "AI Report",
                        report_data: mergedReport,
                    }));
                    dispatch(setActiveReport(finalReportId));
                    buildCompleted = true;
                    return finalReportId;
                } else if (type === "report_error") {
                    const errorMessage = chunkJson?.error?.message || chunkJson?.message || "Report build failed.";
                    reportSnapshot = {
                        ...reportSnapshot,
                        error: chunkJson.error || { message: errorMessage },
                        status_message: errorMessage,
                        build_progress: {
                            ...(reportSnapshot.build_progress || {}),
                            currentStage: "error",
                            log: [
                                ...(reportSnapshot.build_progress?.log || []),
                                {
                                    stage: "error",
                                    message: errorMessage,
                                    timestamp: new Date().toISOString(),
                                },
                            ].slice(-25),
                        },
                    };
                    dispatch(updateReportMessage(clientReportId, {
                        report_status: "failure",
                        report_status_message: errorMessage,
                        report_title: reportSnapshot.title,
                        report_data: reportSnapshot,
                    }));
                    buildCompleted = true;
                    return null;
                }
            }
        }

        if (!buildCompleted) {
            throw new Error("Report stream ended unexpectedly.");
        }

        return null;
    };
    const handleBuildReportClick = async () => {
        const report_id = `report-${Date.now()}`;
        const initialState = createInitialReportState(report_id);
        const initialReportMessage = {
            type: "Harris",
            message_type: "report",
            report_id: report_id,
            report_data: initialState,
            report_status: "processing",
            report_status_message: "Initializing workspace...",
            report_title: "Building the report",
            payload: {
                report_id: report_id,
                report_data: initialState,
                report_status: "processing",
                report_status_message: "Initializing workspace...",
                report_title: "Building the report"
            }
        };
        dispatch(addMessage(initialReportMessage));
        dispatch(setActiveReport(report_id));

        try {
            const data = convertQueryParamsToObject(location?.search || "");
            const page = location.pathname;
            const payload = { data, page };
            await streamReportBuild(report_id, payload, initialState);
        } catch (error) {
            console.error("Build report error:", error);
            const updatedData = {
                report_status: "failure",
                report_status_message: error?.message || "Failed to build report",
                report_title: "Failed to build report",
                report_data: {
                    ...initialState,
                    status_message: error?.message || "Failed to build report",
                    error: { message: error?.message || "Failed to build report" },
                },
            };
            dispatch(updateReportMessage(report_id, updatedData));
        }
    };

    React.useEffect(() => {
        const handleSuggestionSend = (event) => {
            const text = event?.detail?.text;
            if (!text || isAssistantResponding) return;
            performSend(text, { clearInput: true, trigger: "suggestion" });
        };

        window.addEventListener("ai-assistant-send-suggestion", handleSuggestionSend);
        return () => {
            window.removeEventListener("ai-assistant-send-suggestion", handleSuggestionSend);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAssistantResponding]);
    /**
     * Sends a message.
     *
     * Returns once the backend has accepted it, which is not the same as
     * answered. The reply arrives on the conversation channel, so this
     * function has nothing to wait for and nothing to render - which is what
     * lets the page be closed or reloaded while Lou is still working.
     */
    const performSend = async (combinedMessage, { clearInput = true, trigger = "send_button" } = {}) => {
        const startedAt = Date.now();
        const trackingMetadata = {
            inputLength: combinedMessage.length,
            hasExternalNews: isNewsSearchActive,
            trigger,
        };
        if (!combinedMessage.trim()) return;
        if (!conversationId) {
            dispatch(addErrorMessage(AI_ASSISTANT_CONSTANTS.ERRORS.SESSION_UNAVAILABLE));
            return;
        }

        try {
            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.QUESTION_SUBMITTED, trackingMetadata);
            messageSelectedValues.forEach((value) => dispatch(removeSelectedValue(value)));
            if (clearInput) {
                setMessageInput("");
                resetTextareaHeight();
                setIsInputFocused(false);
                if (inputRef.current) inputRef.current.blur();
            }
            dispatch(clearErrorMessage());
            dispatch(clearFollowupSuggestions());

            // The turn is drawn from the id the server hands back rather than
            // waiting for the same news to come round over the channel - the
            // round trip is real and the user is watching. `turnStarted` is
            // idempotent, so the echo lands on a turn already on screen.
            await dispatch(sendUserMessage({
                conversationId,
                text: combinedMessage,
                screenContext: buildCurrentScreenContext(),
            }));

            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.RESPONSE_COMPLETED, {
                ...trackingMetadata,
                responseTimeMs: Date.now() - startedAt,
            });
        } catch (err) {
            console.error("[Lou] Could not send message", err);
            const isBusy = err?.status === 409;
            const code = isBusy ? "SERVER-E-409" : (ErrorCodes["SERVER-E-001"]?.code ?? "SERVER-E-001");
            const message = isBusy
                ? AI_ASSISTANT_CONSTANTS.ERRORS.REQUEST_ALREADY_IN_PROGRESS
                : (err?.message ?? AI_ASSISTANT_CONSTANTS.ERRORS.REQUEST_FAILED);
            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.RESPONSE_FAILED, {
                ...trackingMetadata,
                responseTimeMs: Date.now() - startedAt,
                errorCode: code,
            });
            dispatch(addErrorMessage(`\n[${code}] - ${message}`));
            dispatch(assistantResponding(false));
        }
    };

    /**
     * Stops the running turn.
     *
     * Only the id is needed; the turn is ended on the server and the result
     * comes back over the channel, which is what keeps a stop pressed in one
     * tab from leaving another tab waiting on a reply that is never coming.
     */
    const stopButtonClickHandler = () => {
        if (!conversationId || !activeTurnId) return;
        setStopRequestedFor(activeTurnId);
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.STOP_GENERATION, {
            conversation_id: conversationId,
            turn_id: activeTurnId,
        });
        dispatch(stopCurrentTurn({ conversationId, turnId: activeTurnId }));
    };

    const submitMessageButtonClickHandler = async (trigger = "send_button") => {
        if (!messageInput.trim() && messageSelectedValues.length === 0) return;
        const selectedValuesString = messageSelectedValues.join(", ");
        const combinedMessage =
            selectedValuesString.length > 0
                ? `${messageInput} [${selectedValuesString}]`
                : messageInput;
        await performSend(combinedMessage, { clearInput: true, trigger });
    };
    const messageChangeHandler = (e) => {
        setMessageInput(e.target.value);
        updateVoiceInsertionRange();
        adjustTextareaHeight();
    };
    const updateVoiceInsertionRange = () => {
        const textarea = inputRef.current;
        if (!textarea) return;

        voiceInsertionRangeRef.current = {
            start: textarea.selectionStart ?? messageInput.length,
            end: textarea.selectionEnd ?? messageInput.length,
        };
    };
    const handleVoiceActiveChange = (isActive) => {
        if (isActive) {
            updateVoiceInsertionRange();
        }

        setIsVoiceInputActive(isActive);
    };
    const clampVoiceInsertionIndex = (value, fallback, textLength) => {
        const numericValue = Number(value);
        const numericFallback = Number(fallback);
        const safeValue = Number.isFinite(numericValue) ? numericValue : numericFallback;
        return Math.max(0, Math.min(safeValue, textLength));
    };
    const buildVoiceInsertedText = (currentText, transcript, range) => {
        const selectionStart = clampVoiceInsertionIndex(range?.start, currentText.length, currentText.length);
        const selectionEnd = Math.max(
            selectionStart,
            clampVoiceInsertionIndex(range?.end, selectionStart, currentText.length),
        );
        const before = currentText.slice(0, selectionStart);
        const after = currentText.slice(selectionEnd);
        const selectedText = currentText.slice(selectionStart, selectionEnd);
        const cleanTranscript = transcript.trim();
        const hasSelection = selectedText.length > 0;

        if (!cleanTranscript) {
            return {
                nextText: currentText,
                nextCaretPosition: selectionStart,
            };
        }

        const needsLeadingSpace =
            !hasSelection &&
            before.length > 0 &&
            !/\s$/.test(before) &&
            !/^[,.;:!?)]/.test(cleanTranscript);
        const needsTrailingSpace =
            !hasSelection &&
            after.length > 0 &&
            !/^\s/.test(after) &&
            !/[([{]$/.test(cleanTranscript);
        const insertedText = `${needsLeadingSpace ? " " : ""}${cleanTranscript}${needsTrailingSpace ? " " : ""}`;

        return {
            nextText: `${before}${insertedText}${after}`,
            nextCaretPosition: before.length + insertedText.length,
        };
    };
    const handleVoiceTranscript = (text) => {
        const { nextText, nextCaretPosition } = buildVoiceInsertedText(
            messageInput,
            text,
            voiceInsertionRangeRef.current,
        );

        setMessageInput(nextText);

        window.requestAnimationFrame(() => {
            adjustTextareaHeight();
            inputRef.current?.focus();
            inputRef.current?.setSelectionRange(nextCaretPosition, nextCaretPosition);
            voiceInsertionRangeRef.current = {
                start: nextCaretPosition,
                end: nextCaretPosition,
            };
        });
    };
    const handleNewsSearchToggle = (value) => {
        if (disableNewsSearchButton) return;
        setIsNewsSearchEnabled(value);
    };
    const handleFocus = () => setIsInputFocused(true);
    const handleKeyPress = (event) => {
        if (event.key === "Enter" && messageInput.trim() !== "" && !isAssistantResponding) {
            event.preventDefault();
            submitMessageButtonClickHandler("enter_key");
        }
    };
    const toggleCheckbox = (value) => {
        if (selectedValues.includes(value)) {
            dispatch(removeSelectedValue(value));
        } else {
            dispatch(addSelectedValue(value));
        }
    };
    const toggleNewsSearch = () => toggleCheckbox(NEWS_SEARCH_VALUE);
    const adjustTextareaHeight = () => {
        const textarea = inputRef.current;
        if (!textarea) return;
        textarea.style.height = "auto";
        const maxHeight = 200;
        const newHeight = Math.min(textarea.scrollHeight, maxHeight);
        textarea.style.height = `${newHeight}px`;
        textarea.style.overflowY =
            textarea.scrollHeight > maxHeight ? "auto" : "hidden";
    };
    const getPageSummary = async () => {
        if (isSummaryActionDisabled) return;
        try {
            const userInputMessage = "Summarize this screen's key insights."
            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.SCREEN_SUMMARY_REQUESTED, {
                hasExternalNews: isNewsSearchActive,
            });
            await performSend(userInputMessage, { clearInput: false, trigger: "summary" });
        } catch (err) {
            let code, message;
            if (err.origin === 'backend') {
                const backendError = ErrorCodes["SERVER-E-001"]
                code = backendError?.code ?? "SERVER-E-001";
                message = err?.message ?? "Internal Server error";
            } else {
                const uiError = ErrorCodes["UI-E-001"]
                code = uiError?.code ?? "UI-E-001";
                message = err?.message ?? "Unexpected UI Error. Please contact the admin.";
            }
            dispatch(addErrorMessage(`\n[${code}] - ${message}`));
            dispatch(assistantResponding(false));

        }
    }
    return (
        <form className="card-actions" onSubmit={(e) => e.preventDefault()}>
            <div className="card-footer-wrapper">
                {isNewsSearchActive && (
                    <div className="news-included-pill" role="status">
                        <FiRss className="news-included-pill__icon" aria-hidden />
                        <span>News included in results</span>
                    </div>
                )}
                {isHistoryMetadataContextEnabled && isInputFocused && <HistoryMetaData />}
                <div className="chatbox-shell-wrapper">
                    {isAssistantResponding && <div className="chatbox-thinking-canvas" />}
                    <div
                        className={`card-footer-container chatbox-shell ${
                            isVoiceInputActive ? "chatbox-shell--voice-active" : ""
                        }`}
                    >
                        <div className="card-footer-input-container">
                            <div
                                ref={voicePanelHostRef}
                                className="voice-input-panel-host"
                                aria-hidden={!isVoiceInputActive}
                            />
                            <textarea
                                className="textarea"
                                placeholder={isUnavailable ? UNAVAILABLE_PLACEHOLDER : AI_ASSISTANT_CONSTANTS.INPUT.PLACEHOLDER}
                                disabled={isUnavailable}
                                onKeyDown={handleKeyPress}
                                ref={inputRef}
                                onChange={messageChangeHandler}
                                onClick={updateVoiceInsertionRange}
                                value={messageInput}
                                onFocus={handleFocus}
                                onKeyUp={updateVoiceInsertionRange}
                                onSelect={updateVoiceInsertionRange}
                                rows={1}
                            />
                        </div>
                        <div className="chatbox-controls-row">
                            <div className="chatbox-left-controls" ref={toolsDropdownRef}>
                                <button
                                    type="button"
                                    className="utility-btn utility-btn--icon"
                                    aria-label="More tools coming soon"
                                    disabled
                                >
                                    <FiPlus />
                                </button>
                                <Tooltip content="Tools and options" placement="bottom">
                                    <span>
                                        <button
                                            type="button"
                                            className={`utility-btn utility-btn--pill ${isNewsSearchActive ? "is-active" : ""}`}
                                            disabled={disableNewsSearchButton}
                                            onClick={disableNewsSearchButton ? undefined : () => setToolsDropdownOpen((prev) => !prev)}
                                            aria-disabled={disableNewsSearchButton}
                                            aria-haspopup="true"
                                            aria-expanded={toolsDropdownOpen}
                                            aria-label="Toggle tools menu"
                                        >
                                            <FiSliders />
                                            <span>Tools</span>
                                        </button>
                                    </span>
                                </Tooltip>
                                {shouldShowButton && (
                                    <Tooltip content="Summarize this screen's key insights." placement="bottom">
                                        <span>
                                            <button
                                                type="button"
                                                onClick={isSummaryActionDisabled ? undefined : getPageSummary}
                                                className="utility-btn utility-btn--pill lou-summary-feature"
                                                aria-disabled={isSummaryActionDisabled}
                                                disabled={isSummaryActionDisabled}
                                                data-pendo-feature="Lou-Summary"
                                            >
                                                <FiFileText />
                                                <span>Summary</span>
                                            </button>
                                        </span>
                                    </Tooltip>
                                )}
                                {toolsDropdownOpen && (
                                    <div
                                        className="tools-dropdown"
                                        role="dialog"
                                        aria-label="Tools options"
                                    >
                                        <div className="tools-dropdown__header">Connected Tools</div>
                                        <div className="tools-dropdown__list">
                                            <button
                                                type="button"
                                                className="tool-menu-item lou-external-news-feature"
                                                onClick={() => {
                                                    if (!disableNewsSearchButton) {
                                                        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.TOOL_TOGGLED, {
                                                            toolName: "External News",
                                                            enabled: !isNewsSearchActive,
                                                            state: !isNewsSearchActive ? "enabled" : "disabled",
                                                        });
                                                        toggleCheckbox(NEWS_SEARCH_VALUE);
                                                    }
                                                }}
                                                disabled={disableNewsSearchButton}
                                                data-pendo-feature="Lou-External-News"
                                            >
                                                <div className="tool-menu-item__icon">
                                                    <FiRss />
                                                </div>
                                                <div className="tool-menu-item__content">
                                                    <div className="tool-menu-item__title">External News</div>
                                                    <div className="tool-menu-item__desc">Include real-time news sources</div>
                                                </div>
                                                <div className={`tool-menu-toggle${isNewsSearchActive ? " is-on" : ""}`}>
                                                    <div className="tool-menu-toggle__thumb" />
                                                </div>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="chatbox-right-controls">
                                {isVoiceInputEnabled && (
                                    <VoiceInputControl
                                        disabled={isAssistantResponding}
                                        onTranscript={handleVoiceTranscript}
                                        onActiveChange={handleVoiceActiveChange}
                                        onStatusChange={setVoiceInputStatus}
                                        panelHostRef={voicePanelHostRef}
                                    />
                                )}
                                {voiceInputStatus === VOICE_INPUT_STATUS.IDLE &&
                                    (canStopTurn ? (
                                        <button
                                            className="submit-btn submit-btn--stop"
                                            type="button"
                                            onClick={stopButtonClickHandler}
                                            aria-label={isStopping ? "Stopping" : "Stop generating"}
                                            disabled={isStopping}
                                        >
                                            <FiSquare />
                                        </button>
                                    ) : (
                                        <button
                                            className="submit-btn"
                                            type="button"
                                            onClick={submitMessageButtonClickHandler}
                                            aria-label="Send message"
                                            disabled={isSendDisabled}
                                        >
                                            <FiArrowUp />
                                        </button>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>

                {shouldShowReportBuilderButton && (
                    <div className="chatbox-chip-row">
                        <button
                            type="button"
                            className="chatbox-chip"
                            onClick={disableReportBuilderButton ? undefined : handleBuildReportClick}
                            aria-disabled={disableReportBuilderButton}
                            disabled={disableReportBuilderButton}
                        >
                            <FiBarChart2 className="chip-icon" />
                            Build Report
                        </button>
                    </div>
                )}

                <div className="ai-disclaimer" role="note">
                    <div className="ai-disclaimer-text">Responses are generated by AI and may contain mistakes. Please verify important details.</div>
                </div>
            </div>
        </form>
    );
};
export default MessageInput;
